import { showNotification } from '@mantine/notifications';
import { useApiGetEntityTeams } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import { DEFAULT_ITEM_SIZE } from '@repo/common/components/MultiSelector/constants';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { EntityType } from '@repo/common/enums/entityType';
import { map, noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useAuthUser, type IIntegration, type ISecodaEntity } from '../../api';
import { useFeatureFlags } from '../../utils/featureFlags';
import type { PropertySelectorProps } from './PropertySelector';
import { PropertySelector } from './PropertySelector';
export interface TeamsPropertySelectorProps
	extends Pick<
		PropertySelectorProps,
		'onChange' | 'position' | 'withinPortal'
	> {
	entity: ISecodaEntity;
	viewerOfEntity: boolean;
	integration?: IIntegration;
	noMaxHeightDropdown?: boolean;
}

export function TeamsPropertySelector({
	entity,
	viewerOfEntity,
	integration,
	onChange,
	withinPortal,
	noMaxHeightDropdown = false,
	...propertySelectorProps
}: TeamsPropertySelectorProps) {
	const { isGuestUser } = useAuthUser();
	const isChild =
		entity.parent !== null &&
		entity.integration === null &&
		(entity.entity_type === EntityType.glossary ||
			entity.entity_type === EntityType.collection ||
			entity.entity_type === EntityType.document);

	const { teamsPermissionsV2Frontend } = useFeatureFlags();

	const { data: teams } = useApiListTeamsV2({
		queryParams: {
			only_joined: isGuestUser,
		},
	});

	// TODO: use useSuspenseApiGetEntityTeams once teams-permissions-v2-frontend is fully rolled out
	const { data: inheritedTeamsBreakdown } = useApiGetEntityTeams(
		{
			queryParams: {
				entity_id: entity.id,
			},
		},
		{
			enabled: teamsPermissionsV2Frontend,
		}
	);

	const teamOptions = useMemo(
		() =>
			map(teams, (team) => ({
				label: team.name,
				value: team.id,
				icon: team.icon,
				navigateTo: '/teams',
				onClick: noop,
			})),
		[teams]
	);

	const validateSelection = useCallback(
		(
			item: SelectablePropertyItem,
			currentSelection: SelectablePropertyItem[]
		) => {
			if (isChild) {
				const isCurrentlySelected = currentSelection.some(
					(s) => s.value === item.value
				);
				const wouldBeNewSelection = !isCurrentlySelected;

				if (wouldBeNewSelection) {
					// Show notification and prevent selection
					const parentTeam = teamOptions.find((team) =>
						entity.teams?.includes(team.value)
					);

					showNotification({
						title: 'Cannot change teams',
						message: `This term cannot be moved to ${item.label || 'a different team'} as the parent is in ${parentTeam?.label || 'a different team'}. Please remove the parent of the child term to change teams.`,
						color: 'red',
					});

					return false;
				}
			}
			return true;
		},
		[isChild, teamOptions, entity.teams]
	);

	const inheritedTeams: string[] = useMemo(
		() =>
			teamsPermissionsV2Frontend
				? (Array.from(
						new Set(
							inheritedTeamsBreakdown?.ancestor_inheritance
								.concat(inheritedTeamsBreakdown?.descendant_inheritance)
								.concat(inheritedTeamsBreakdown?.integration_inheritance)
						)
					) ?? [])
				: (integration?.teams ?? []),
		[inheritedTeamsBreakdown, teamsPermissionsV2Frontend, integration?.teams]
	);

	const inheritedTeamsTooltip: Record<string, string> | string = useMemo(() => {
		if (!teamsPermissionsV2Frontend) {
			return `From ${integration?.name}`;
		}
		const tooltip: Record<string, string> = {};
		for (const team of inheritedTeamsBreakdown?.ancestor_inheritance ?? []) {
			tooltip[team] = 'Inherited from 1 or more ancestors';
		}
		for (const team of inheritedTeamsBreakdown?.integration_inheritance ?? []) {
			tooltip[team] = `Inherited from ${integration?.name}`;
		}
		for (const team of inheritedTeamsBreakdown?.descendant_inheritance ?? []) {
			tooltip[team] = 'Required by 1 or more descendants';
		}
		return tooltip;
	}, [inheritedTeamsBreakdown, integration?.name, teamsPermissionsV2Frontend]);

	return (
		<PropertySelector
			inheritedValues={inheritedTeams ?? []}
			inheritedValuesTooltip={inheritedTeamsTooltip}
			selected={entity?.teams ?? []}
			type="multi"
			value="teams"
			iconType="emoji"
			options={teamOptions}
			isViewerUser={viewerOfEntity}
			readOnly={viewerOfEntity}
			onChange={onChange}
			onValidateSelection={validateSelection}
			returnInheritedValuesOnEmpty={false}
			withinPortal={withinPortal}
			noMaxHeightDropdown={noMaxHeightDropdown}
			virtuosoHeight={`min(70vh, ${(teamOptions.length || 8) * DEFAULT_ITEM_SIZE}px)`}
			{...propertySelectorProps}
		/>
	);
}
